import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useThree } from '@react-three/fiber';
import {
  createCoordinates,
  createSelectCoordinates,
  isDrawable,
  isSelectable
} from '../utils/utils';
import { stageParameterState, ySymmetryState } from 'features/morphing/state/stageParameterAtoms';

export const useBoxViewerData = () => {
  const stageParameter = useRecoilValue(stageParameterState);
  const ySymmetry = useRecoilValue(ySymmetryState);
  const { scene } = useThree();

  const drawable = useMemo(
    () => isDrawable(
      stageParameter.controlPoints,
      stageParameter.morphingMin,
      stageParameter.morphingMax
    ),
    [stageParameter]
  );

  const selectable = useMemo(
    () => isSelectable(
      stageParameter.targetControlPointsX,
      stageParameter.targetControlPointsY,
      stageParameter.targetControlPointsZ,
      stageParameter.controlPoints
    ),
    [stageParameter]
  );

  const { coordinates, selectedCoordinates, remainder } = useMemo(() => {
    if (!drawable) return { coordinates: [], selectedCoordinates: [], remainder: [] };

    if (selectable) {
      const { selectedCoordinates, remainder } = createSelectCoordinates(
        stageParameter.controlPoints,
        stageParameter.morphingMin,
        stageParameter.morphingMax,
        stageParameter.targetControlPointsX,
        stageParameter.targetControlPointsY,
        stageParameter.targetControlPointsZ,
        ySymmetry
      );
      return {
        coordinates: createCoordinates(
          stageParameter.controlPoints,
          stageParameter.morphingMin,
          stageParameter.morphingMax),
        selectedCoordinates,
        remainder
      };
    } else {
      return {
        coordinates: createCoordinates(
          stageParameter.controlPoints,
          stageParameter.morphingMin,
          stageParameter.morphingMax
        ),
        selectedCoordinates: [],
        remainder: []
      };
    }
  }, [
    drawable,
    selectable,
    stageParameter,
    ySymmetry
  ]);

  return { scene, drawable, selectable, coordinates, selectedCoordinates, remainder };
};
